// client/src/components/Insights/tabs/SpendingTab.jsx
// Spending patterns in the chosen period. Only flexible spending counts: rent,
// bills, debt payments, savings and transfers are left out.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CardContent, Chip, Grid, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell } from 'recharts';
import { CalendarDays, Coffee, Compass, Package, Wallet } from 'lucide-react';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { EmptyState, GlassCard, LoadingGrid, SectionTitle, StatCard } from '../shared/SharedComponents';
import { alpha } from '../shared/constants';
import { getImpulseSpending } from '../../../services/insightsAPI';
import { useInsightsFilter } from '../shared/InsightsFilterContext';

// Monday first, as in most of Europe.
const ORDER = [1, 2, 3, 4, 5, 6, 0];

const SpendingTab = () => {
  const { t, i18n } = useTranslation();
  const { formatAmount } = useCurrency();
  const { from, to } = useInsightsFilter();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['spendingPatterns', from, to],
    queryFn: () => getImpulseSpending({ from, to }),
    select: (d) => d?.data || d,
  });

  if (isLoading) return <LoadingGrid n={4} height={120} />;
  if (error) {
    return (
      <Alert severity="warning" action={<Button onClick={refetch} size="small">{t('aiInsights.common.retry', 'Retry')}</Button>}>
        {t('aiInsights.spending.loadError', 'Could not load your spending patterns.')}
      </Alert>
    );
  }
  if (!data || !data.count) {
    return (
      <EmptyState
        icon={Compass}
        title={t('aiInsights.spending.emptyTitle', 'No flexible spending in this period')}
        subtitle={t('aiInsights.spending.emptySubtitle', 'Pick a longer period above, or come back after a few weeks of tracking.')}
      />
    );
  }

  const dayName = (d) => new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 4 + d)));
  const weekdays = ORDER.map((d) => ({ ...data.weekdays[d], label: dayName(d), weekend: d === 0 || d === 6 }));
  const date = (iso) => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(new Date(iso));

  // Plain observations from the numbers, most useful first.
  const notes = [];
  const grew = data.categories.filter((c) => c.change != null && c.change >= 25 && c.amount >= 30);
  grew.slice(0, 2).forEach((c) => notes.push(t('aiInsights.spending.notes.grew', {
    name: c.name || t('aiInsights.spending.uncategorised', 'Uncategorised'),
    pct: c.change,
    defaultValue: '{{name}} is up {{pct}}% on the period before.',
  })));
  if (data.weekend.share >= 45) {
    notes.push(t('aiInsights.spending.notes.weekend', { pct: data.weekend.share, defaultValue: 'Weekends take {{pct}}% of your flexible spending. A weekend budget could help.' }));
  }
  if (data.small.perMonth >= 40) {
    notes.push(t('aiInsights.spending.notes.small', { amount: formatAmount(data.small.perMonth), defaultValue: 'Purchases under 10 add up to about {{amount}} a month.' }));
  }
  if (data.delivery.perMonth >= 50) {
    notes.push(t('aiInsights.spending.notes.delivery', { amount: formatAmount(data.delivery.perMonth), defaultValue: 'Delivery and online shopping come to about {{amount}} a month.' }));
  }

  return (
    <Box>
      <SectionTitle icon={Compass}>{t('aiInsights.spending.title', 'Spending patterns')}</SectionTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('aiInsights.spending.lead', 'Only flexible spending counts here: rent, bills, debt payments, savings and transfers are left out.')}
      </Typography>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard
            label={t('aiInsights.spending.flexible', 'Flexible spending')}
            value={formatAmount(data.flexible)}
            sub={t('aiInsights.spending.ofTotal', { pct: data.flexibleShare, defaultValue: '{{pct}}% of all spending' })}
            icon={Wallet}
            color="#8b5cf6"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={t('aiInsights.spending.weekend', 'Weekends')}
            value={`${data.weekend.share}%`}
            sub={formatAmount(data.weekend.amount)}
            icon={CalendarDays}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={t('aiInsights.spending.small', 'Small purchases')}
            value={formatAmount(data.small.amount)}
            sub={t('aiInsights.spending.count', { count: data.small.count, defaultValue: '{{count}} purchases' })}
            icon={Coffee}
            color="#06b6d4"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={t('aiInsights.spending.delivery', 'Delivery & online')}
            value={formatAmount(data.delivery.amount)}
            sub={t('aiInsights.spending.count', { count: data.delivery.count, defaultValue: '{{count}} purchases' })}
            icon={Package}
            color="#ef4444"
          />
        </Grid>
      </Grid>

      {notes.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {notes.map((n) => <Typography key={n} variant="body2">{n}</Typography>)}
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={5}>
          <GlassCard sx={{ height: '100%' }}>
            <CardContent>
              <Typography fontWeight={800} mb={1}>{t('aiInsights.spending.byDay', 'By day of the week')}</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weekdays}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis hide />
                  <RTooltip formatter={(v) => formatAmount(v)} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {weekdays.map((d) => <Cell key={d.day} fill={d.weekend ? '#f59e0b' : alpha('#8b5cf6', 0.6)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <GlassCard sx={{ height: '100%' }}>
            <CardContent>
              <Typography fontWeight={800} mb={1}>{t('aiInsights.spending.categories', 'Where it goes')}</Typography>
              <Stack spacing={1}>
                {data.categories.map((c) => (
                  <Stack key={c.categoryId || 'none'} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{c.name || t('aiInsights.spending.uncategorised', 'Uncategorised')}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('aiInsights.spending.share', { pct: c.share, defaultValue: '{{pct}}% of flexible spending' })}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {c.change != null && (
                        <Chip
                          size="small"
                          label={`${c.change > 0 ? '+' : ''}${c.change}%`}
                          sx={{ fontWeight: 700, bgcolor: alpha(c.change > 10 ? '#ef4444' : c.change < -10 ? '#10b981' : '#9ca3af', 0.15) }}
                        />
                      )}
                      <Typography variant="body2" fontWeight={800}>{formatAmount(c.amount)}</Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
              {data.hasPrevious && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                  {t('aiInsights.spending.changeNote', 'Change is against the same number of days just before this period.')}
                </Typography>
              )}
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>

      {data.oneOffs.length > 0 && (
        <GlassCard>
          <CardContent>
            <Typography fontWeight={800}>{t('aiInsights.spending.oneOffs', 'Bigger one-off purchases')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('aiInsights.spending.oneOffsLead', 'Several times your usual purchase. Planned or not, they shape the month.')}
            </Typography>
            <List dense>
              {data.oneOffs.map((o, i) => (
                <ListItem key={i} sx={{ px: 0 }}>
                  <ListItemText primary={o.description || o.category} secondary={[date(o.date), o.category].filter(Boolean).join(' · ')} />
                  <Typography fontWeight={800}>{formatAmount(o.amount)}</Typography>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </GlassCard>
      )}
    </Box>
  );
};

export default SpendingTab;
